import Icon, {
  CheckCircleFilled,
  CloseCircleFilled,
  DownOutlined,
  PlusOutlined,
  RightOutlined,
  CloseCircleOutlined,  
  SearchOutlined
} from '@ant-design/icons'
import { Button, Form, Table, message, Input } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import useToken from 'antd/es/theme/useToken'
import dayjs from 'dayjs'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ReactComponent as PatientIconFilled } from '../../assets/Icons_ Patient Fill.svg'
import { ReactComponent as BpIcon } from '../../assets/Icons_BP Fill.svg'
import { ReactComponent as RespIcon } from '../../assets/Icons_Lungs Fill.svg'
import { ReactComponent as PulseIcon } from '../../assets/Icons_Normal symbol Fill.svg'
import { ReactComponent as Spo2Icon } from '../../assets/Icons_Oxygen Fill.svg'
import { ReactComponent as TempIcon } from '../../assets/Icons_Temperature Fill.svg'
import { ReactComponent as EcgIcon } from '../../assets/ecg.svg'
import ActionDropdown, {
  ActionDropdownItemsType,
} from '../../components/ActionDropdown/ActionDropdown'
import CountdownButton from '../../components/CountdownButton/CountdownButton'
import { displayMessage } from '../../helpers/Message'
import {
  activatePatient,
  addPatientWithPatientKey,
  deletePatient,
  errorPatient,
  getPatients,
  selectAllPatients,
  selectErrorInPatient,
  selectPatientByKey,
  selectSuccessInPatient,
  selectPatientLoadingState,
  setPatientThresholdForUpdate,
  successPatient,
  updatePatientThreshold,
} from '../../state/patient/patientReducer'
import { Patient } from '../../state/patient/types'
import { getPatientAdmissions } from '../../state/patientAdmission/patientAdmissionReducer'
import { useGetPatientAdmissionsMewsScoreDataQuery } from '../../state/patientAdmissionMews/api'
import { addPatientAlertConfiguration } from '../../state/patientAlertConfiguration/patientAlertConfigurationReducer'
import { PatientViewDataType } from '../../types/PatientViewDataType'
import Header from '../CyHeader/CyHeader'
import ConfigureThresholdView from '../NewPatientView/ConfigureThresholdView'
import NewPatientView from './../NewPatientView/NewPatientView'
import { PatientTabs } from './PatientTabs'
import {
  MewsScoreTag,
  PatientViewContainer,
  VitalStatusContainer,
  VitalStatusIcon,
  generateFullName,
  getColor,
} from './PatientsViewStyle'

export const VitalStatus = ({
  patientKey,
  size,
}: {
  patientKey: string
  size: 'small' | 'medium' | 'large'
}) => {
  let patientAdmissionMewsScore = useGetPatientAdmissionsMewsScoreDataQuery(
    {},
    {
      selectFromResult: (state) => {
        let mewsScoreDatas = state.data?.mewsScores
        let currentMewsScoreData = mewsScoreDatas?.find(
          (data) => data.patientKey === patientKey
        )
        return {
          ...currentMewsScoreData,
        }
      },
    }
  )

  return (
    <VitalStatusContainer>
      <VitalStatusIcon
        iconsize={size}
        component={PulseIcon}
        style={{ color: getColor(patientAdmissionMewsScore?.pr) }}
      />
      <VitalStatusIcon
        iconsize={size}
        component={Spo2Icon}
        style={{ color: getColor(patientAdmissionMewsScore?.spo2) }}
      />
      <VitalStatusIcon
        iconsize={size}
        component={RespIcon}
        style={{ color: getColor(patientAdmissionMewsScore?.resp) }}
      />
      <VitalStatusIcon
        iconsize={size}
        component={TempIcon}
        style={{ color: getColor(patientAdmissionMewsScore?.temp) }}
      />
      <VitalStatusIcon
        iconsize={size}
        component={BpIcon}
        style={{ color: getColor(patientAdmissionMewsScore?.bp) }}
      />
    </VitalStatusContainer>
  )
}

export const EcgStatus = ({
  patientKey,
  size,
}: {
  patientKey: string
  size: 'small' | 'medium' | 'large'
}) => {
  let patientAdmissionMewsScore = useGetPatientAdmissionsMewsScoreDataQuery(
    {},
    {
      selectFromResult: (state) => {
        let mewsScoreDatas = state.data?.mewsScores
        let currentMewsScoreData = mewsScoreDatas?.find(
          (data) => data.patientKey === patientKey
        )
        return {
          ...currentMewsScoreData,
        }
      },
    }
  )

  return (
    <VitalStatusContainer>
      <VitalStatusIcon
        iconsize={size}
        component={EcgIcon}
        style={{ color: getColor(patientAdmissionMewsScore?.ecg) }}
      />
    </VitalStatusContainer>
  )
}

export const MewsScore = ({ patientKey }: { patientKey: string }) => {
  let data = useGetPatientAdmissionsMewsScoreDataQuery(
    {},
    {
      selectFromResult: (state) => {
        let mewsScoreDatas = state.data?.mewsScores
        let currentMewsScoreData = mewsScoreDatas?.find(
          (data) => data.patientKey === patientKey
        )
        return {
          mewsScore: currentMewsScoreData?.mewsScore,
        }
      },
    }
  )
  let mewsScore = data?.mewsScore
  let color: string = 'white'
  let message: 'Normal' | 'Warning' | 'Critical' | 'Urgent' | 'Offline' =
    'Offline'
  if (!mewsScore && mewsScore !== 0) {
    color = '#808080'
    message = 'Offline'
  } else if (mewsScore < 0) {
    color = '#FFFFFF'
    message = 'Offline'
  } else if (mewsScore === 0) {
    color = '#00FF00'
    message = 'Normal'
  } else if (mewsScore === 1) {
    color = '#FFEC3D'
    message = 'Warning'
  } else if (mewsScore === 2) {
    color = '#FFA500'
    message = 'Urgent'
  } else if (mewsScore >= 3) {
    color = '#FF0000'
    message = 'Critical'
  }

  return <MewsScoreTag bgColor={color}>{message}</MewsScoreTag>
}

type PatientContextType = {
  updateViewState: boolean
  configureAlertViewState: boolean
  setOpenViewStates: React.Dispatch<
    React.SetStateAction<
      Record<
        string,
        {
          updateState: boolean
          configureAlertState: boolean
        }
      >
    >
  >
}

export const PatientContext = createContext<PatientContextType | undefined>(
  undefined
)

export const usePatientContext = (): PatientContextType => {
  const context = useContext(PatientContext)
  return context!
}

function PatientView() {
  const dispatch = useDispatch()
  const [theme, token] = useToken()
  const [patientFormControl] = Form.useForm()
  const [alertConfigurationFormControl] = Form.useForm()
  const [thresholdFormControl] = Form.useForm()
  const [messageApi, contextHolder] = message.useMessage()
  const [openNewPatientDrawer, setOpenNewPatientDrawer] = useState(false)
  const [searchFilters, setSearchFilters] = useState<{ [key: string]: string }>({
    patientId: '',
    patientName: '',    
    doctor: ''   
  });
  
  const [activeSearch, setActiveSearch] = useState<{ [key: string]: boolean }>({
    patientId: false,
    patientName: false,    
    doctor: false   
  });
  
  const [tableScroll, setTableScroll] = useState<{ x?: number; y?: number }>({ y: 0 });

  // Function to handle search input
const handleSearch = (key: string, value: string) => {
  setSearchFilters((prev) => ({ ...prev, [key]: value }));
};

// Function to toggle search input visibility
const toggleSearch = (key: string) => {
  setActiveSearch((prev) => ({ ...prev, [key]: true }));
};

// Function to hide search input when blurred
const handleBlur = (key: string) => {
  if (!searchFilters[key]) {
    setActiveSearch((prev) => ({ ...prev, [key]: false }));
  }
};

const resetSearchField = (key: string) => {
  setSearchFilters((prev) => ({ ...prev, [key]: '' }));
};
  const [openUpdateThresholdDrawer, setOpenUpdateThresholdDrawer] =
    useState(false)
  useEffect(() => {
    dispatch(getPatients())
  }, [dispatch])
  const refreshTimeInSeconds = 30
  const { data: PatientMewsScoreData, refetch } =
    useGetPatientAdmissionsMewsScoreDataQuery({})
  const onCountdownFinish = () => {
    refetch()
  }

  const patients = useSelector(selectAllPatients)
  const patientError = useSelector(selectErrorInPatient)
  const patientSuccess = useSelector(selectSuccessInPatient)
  const patientLoadingStatus = useSelector(selectPatientLoadingState)
  function intialOpenViewStates() {
    let data: Record<
      string,
      {
        updateState: boolean
        configureAlertState: boolean
      }
    > = {}
    patients?.forEach((patient) => {
      data[patient.patientKey] = {
        updateState: false,
        configureAlertState: false,
      }
    })
    return data
  }
  const [openViewStates, setOpenViewStates] = useState(intialOpenViewStates())
  const [expandedKey, setExpandedKey] = useState<string>('')
  useEffect(() => {
    dispatch(getPatientAdmissions())
  }, [dispatch, patients])

  const updateEvent = async (patientKey: string) => {
    setCurrentKey(patientKey)
    let oldExpandedElement = document.querySelector(
      `tr[data-row-key="${expandedKey}"]`
    ) as HTMLElement | null
    if (oldExpandedElement) oldExpandedElement.style.background = ''
    setExpandedKey(expandedKey === patientKey ? '' : patientKey)
    if (expandedKey === patientKey) {
      let ExpandedElement = document.querySelector(
        `tr[data-row-key="${patientKey}"]`
      ) as HTMLElement | null
      if (ExpandedElement) ExpandedElement.style.background = '#e3e3e3'
    }
    setOpenViewStates((prev) => {
      const newData = { ...prev }
      newData[patientKey] = {
        configureAlertState: false,
        updateState: true,
      }
      return newData
    })
  }

  const configureAlertsEvent = async (patientKey: string) => {
    let oldExpandedElement = document.querySelector(
      `tr[data-row-key="${expandedKey}"]`
    ) as HTMLElement | null
    if (oldExpandedElement) oldExpandedElement.style.background = ''
    setExpandedKey(expandedKey === patientKey ? '' : patientKey)
    if (expandedKey === patientKey) {
      let ExpandedElement = document.querySelector(
        `tr[data-row-key="${patientKey}"]`
      ) as HTMLElement | null
      if (ExpandedElement) ExpandedElement.style.background = '#e3e3e3'
    }
    setOpenViewStates((prev) => {
      const newData = { ...prev }
      newData[patientKey] = {
        configureAlertState: true,
        updateState: false,
      }
      return newData
    })
  }
  const configureThresholdEvent = (patientKey: string) => {
    setCurrentKey(patientKey)
    dispatch(
      setPatientThresholdForUpdate({
        form: thresholdFormControl,
        key: patientKey,
      })
    )
    setOpenUpdateThresholdDrawer(true)
  }

  const PatientActionDropdown = ({ patientKey }: { patientKey: string }) => {
    const currentPatient = useSelector(selectPatientByKey(patientKey))
    const currentStatus = currentPatient?.status === 'N'
    const activateStatus = !currentStatus
    const items: ActionDropdownItemsType[] = [
      {
        type: 'toggle',
        onClick: () => {
          dispatch(activatePatient(currentPatient!.patientKey))
        },
        label: 'Reactivate',
        disabled: activateStatus,
        switchState: activateStatus,
      },
      {
        type: 'normal',
        label: 'Configure Threshold',
        disabled: !activateStatus,
        onClick: () => configureThresholdEvent(patientKey),
      },
      {
        type: 'normal',
        label: 'Configure Alerts',
        disabled: !activateStatus,
        onClick: () => {
          configureAlertsEvent(patientKey)
        },
      },
      {
        type: 'normal',
        label: 'Update',
        disabled: !activateStatus,
        onClick: () => {
          updateEvent(patientKey)
        },
      },
      {
        type: 'confirm',
        onClick: () => {
          dispatch(deletePatient(currentPatient!.patientKey))
        },
        popConfirmTitle: 'Are you Sure to Delete?',
        label: 'Delete',
        disabled: currentStatus,
      },
    ]

    return <ActionDropdown actionDropdownItems={items} label='...' />
  }

  const [currentKey, setCurrentKey] = useState('')

  const StatusIcon = ({ info }: { info: string }) => {
    const statusIcons: Record<string, JSX.Element> = {
      Y: <CheckCircleFilled style={{ color: 'green', fontSize: '30px' }} />,
      N: <CloseCircleFilled style={{ color: 'red', fontSize: '30px' }} />,
    }
    return <>{statusIcons[info]}</>
  }

  const columns: ColumnsType<PatientViewDataType> = [
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Patient Id
          {activeSearch.patientId ? (
            <Input
              autoFocus
              placeholder="Search"
              value={searchFilters.patientId}
              onChange={(e) => handleSearch('patientId', e.target.value)}
              onBlur={() => handleBlur('patientId')}
              style={{ width: '100%' }}
              suffix={
                searchFilters.patientId ? (
                  <CloseCircleOutlined
                    onClick={() => resetSearchField('patientId')}
                    style={{ cursor: 'pointer', color: '#999' }}
                  />
                ) : null
              }
            />
          ) : (
            <SearchOutlined
              style={{ cursor: 'pointer' }}
              onClick={() => toggleSearch('patientId')}
            />
          )}
        </div>
      ),
      dataIndex: 'patientId',
      key: 'id',
      width: '180px',
      sorter: (a, b) => a.patientId.localeCompare(b.patientId),
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Patient Name
          {activeSearch.patientName ? (
            <Input
              autoFocus
              placeholder="Search"
              value={searchFilters.patientName}
              onChange={(e) => handleSearch('patientName', e.target.value)}
              onBlur={() => handleBlur('patientName')}
              style={{ width: '150px' }}
              suffix={
                searchFilters.patientName ? (
                  <CloseCircleOutlined
                    onClick={() => resetSearchField('patientName')}
                    style={{ cursor: 'pointer', color: '#999' }}
                  />
                ) : null
              }
            />
          ) : (
            <SearchOutlined
              style={{ cursor: 'pointer' }}
              onClick={() => toggleSearch('patientName')}
            />
          )}
        </div>
      ),
      dataIndex: 'patientName',
      key: 'name',
      sorter: (a, b) => a.patientName.localeCompare(b.patientName),
      width: '180px',
    },
    {
      title: 'Patient Vitals',
      width: '180px',
      dataIndex: 'patientKey',
      key: 'patientKey',
      render: (data) => (
        <VitalStatus size='large' patientKey={data}></VitalStatus>
      ),
    },
    {
      title: 'ECG',
      dataIndex: 'patientKey',
      key: 'patientKey',
      render: (data) => <EcgStatus size='large' patientKey={data} />,
      width: '120px',
    },
    {
      title: 'Bed | Ward',
      dataIndex: 'bed_ward',
      key: 'bed_ward',
      width: '120px',
      sorter: {
        compare: (a, b) => a.bed_ward.localeCompare(b.bed_ward),
      },
    },
    {
      title: 'Age',
      dataIndex: 'age',
      key: 'age',
      width: '80px',
    },
    {
      title: 'Gender',
      dataIndex: 'gender',
      key: 'gender',
      width: '80px',
      sorter: {
        compare: (a, b) => a.gender.localeCompare(b.gender),
      },     
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Doctor
          {activeSearch.doctor ? (
            <Input
              autoFocus
              placeholder="Search"
              value={searchFilters.doctor}
              onChange={(e) => handleSearch('doctor', e.target.value)}
              onBlur={() => handleBlur('doctor')}
              style={{ width: '120px' }}
              suffix={
                searchFilters.doctor ? (
                  <CloseCircleOutlined
                    onClick={() => resetSearchField('doctor')}
                    style={{ cursor: 'pointer', color: '#999' }}
                  />
                ) : null
              }
            />
          ) : (
            <SearchOutlined
              style={{ cursor: 'pointer' }}
              onClick={() => toggleSearch('doctor')}
            />
          )}
        </div>
      ),
      dataIndex: 'doctor',
      key: 'doctor',
      sorter: (a, b) => a.doctor.localeCompare(b.doctor),
      width: '120px',
    },
    {
      title: 'MEWS status',
      dataIndex: 'patientKey',
      key: 'patientKey',
      render: (patientKey) => <MewsScore patientKey={patientKey}></MewsScore>,
      width: '100px',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (info: string) => <StatusIcon info={info} />,
      className: 'ant-center',
      width: '100px',
      sorter: {
        compare: (a, b) => a.status.localeCompare(b.status),
      },
      defaultSortOrder: 'descend',
    },
    {
      title: 'Action',
      dataIndex: 'patientKey',
      key: 'x',
      width: '90px',
      className: 'ant-center',
      render: (patientKey) => <PatientActionDropdown patientKey={patientKey} />,
    },
  ]

  useEffect(() => {
    const updateScroll = () => {        
      setTableScroll(
        window.innerWidth <= 1200
          ? { x: 120 * columns.length, y: 0 } 
          : { y: 0 } 
      );
    };
    
    updateScroll();
    window.addEventListener('resize', updateScroll);
    return () => window.removeEventListener('resize', updateScroll);
  }, [columns.length]);
  const patientsViewTableData: PatientViewDataType[] = useMemo(() => {
    const sortedPatientData = [...patients].sort((a, b) =>
      a.firstName.localeCompare(b.firstName)
    )
    let patientViewTableData: PatientViewDataType[] = sortedPatientData.map(
      (item: Patient, index) => {
        const name = generateFullName(item.title, item.firstName, item.lastName)
        const getGender = (string: string | null | undefined) => {
          if (string === 'M') return 'Male'
          else if (string === 'F') return 'Female'
          return ' -'
        }

        let mewsScoreData = PatientMewsScoreData?.mewsScores?.find(
          (mewsScoreData) => mewsScoreData.patientKey === item.patientKey
        )
        let mewsScore = mewsScoreData?.mewsScore
        let data = {
          key: item.patientKey,
          patientId: item.patientId,
          patientKey: item.patientKey,
          patientName: name,
          age: item.age,
          gender: getGender(item.sex),
          dob: item.dob,
          phone: item.emergencyPhone,
          secPhone: item.emergencyPhone2,
          doctor: item.assignedDoctor,
          bed_ward: item.bed_ward,
          status: item.status,
          mews: mewsScore === 0 || mewsScore ? mewsScore : -1,
        }

        return data
      }
    )

    //sort based on mews Score
    patientViewTableData = [...patientViewTableData].sort(
      (a, b) => b.mews - a.mews
    )
    return patientViewTableData
  }, [patients, PatientMewsScoreData])

  const filteredPatients = useMemo(() => {
    return (patientsViewTableData || []).filter((patient) => {
      return (
        (patient?.patientId?.toLowerCase() || "").includes(searchFilters?.patientId?.toLowerCase() || "") &&
        (patient?.patientName?.toLowerCase() || "").includes(searchFilters?.patientName?.toLowerCase() || "") &&
        (patient?.doctor?.toLowerCase() || "").includes(searchFilters?.doctor?.toLowerCase() || "")
      );
    });
  }, [searchFilters, patientsViewTableData]);

  const onNewPatientViewClose = () => {
    setOpenNewPatientDrawer(false)
    patientFormControl.resetFields()
  }

  const onUpdateThresholdViewClose = () => {
    setOpenUpdateThresholdDrawer(false)
    thresholdFormControl.resetFields()
  }

  const [currentPatientKey, setCurrentPatientKey] = useState('')
  const onFinishPatientDetails = async () => {
    const { countryCode, countryCode2, ...patientFormValues } =
      patientFormControl.getFieldsValue()
    const patient = {
      ...patientFormValues,
      emergencyPhone: countryCode + patientFormValues.emergencyPhone,
      emergencyPhone2: countryCode2 + patientFormValues.emergencyPhone2,
      dob: dayjs(patientFormValues.dob).format('YYYY-MM-DD'),
      status: 'Y'
    }
    dispatch(
      addPatientWithPatientKey({
        patient: patient,
        setPatientKey: setCurrentPatientKey,
      })
    )
  }

  const onFinishConfigureThreshold = async () => {
    const alertConfigurationFormValues =
      alertConfigurationFormControl.getFieldsValue()
    const alertConfiguration = {
      patientKey: currentPatientKey,
      alertConfiguration: alertConfigurationFormValues,
    }

    dispatch(addPatientAlertConfiguration(alertConfiguration))
    // onNewPatientViewClose()
  }

  const onFinishUpdateThreshold = () => {
    const values = thresholdFormControl.getFieldsValue()
    dispatch(
      updatePatientThreshold({
        key: currentKey,
        thresholdData: values,
      })
    )
    onUpdateThresholdViewClose()
    thresholdFormControl.resetFields()
  }

  useEffect(() => {
    if (patientError) {
      displayMessage(patientError, 'error', messageApi)
      dispatch(errorPatient(null))
    }
    if (patientSuccess) {
      displayMessage(patientSuccess, 'success', messageApi)
      dispatch(successPatient(null))
    }
  }, [patientError, patientSuccess])

  return (
    <>
      <Header
        title={
          <>
            <Icon component={PatientIconFilled} style={{ fontSize: '150%' }} />
            <h2>Patients</h2>
            <CountdownButton
              size='middle'
              countDownTimeInSeconds={refreshTimeInSeconds}
              label='Auto refreshing in'
              onCountdownFinish={onCountdownFinish}
            />
          </>
        }
      />
      <PatientViewContainer>
        <NewPatientView
          onFinishConfigureThreshold={onFinishConfigureThreshold}
          onFinishPatientDetails={onFinishPatientDetails}
          open={openNewPatientDrawer}
          onClose={onNewPatientViewClose}
          configureAlertForm={alertConfigurationFormControl}
          patientForm={patientFormControl}
        />
        <ConfigureThresholdView
          onClose={onUpdateThresholdViewClose}
          onFinish={onFinishUpdateThreshold}
          form={thresholdFormControl}
          open={openUpdateThresholdDrawer}
        />
        {contextHolder}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
          <Button
            type='dashed'
            icon={<PlusOutlined />}
            onClick={() => {
              patientFormControl.resetFields()
              if (openNewPatientDrawer) setOpenNewPatientDrawer(false)
              else setOpenNewPatientDrawer(true)
            }}
          >
            Register Patient
          </Button>
        </div>      
        <Table
          loading={patientLoadingStatus}
          className='pageDataTable'
          columns={columns}
          expandable={{
            expandRowByClick: true,
            expandedRowKeys: [expandedKey],
            onExpand(expanded, record) {
              let oldExpandedElement = document.querySelector(
                `tr[data-row-key="${expandedKey}"]`
              ) as HTMLElement | null
              if (oldExpandedElement) oldExpandedElement.style.background = ''
              setExpandedKey(
                expandedKey === record.patientKey ? '' : record.patientKey
              )
              if (expanded) {
                let ExpandedElement = document.querySelector(
                  `tr[data-row-key="${record.patientKey}"]`
                ) as HTMLElement | null
                if (ExpandedElement)
                  ExpandedElement.style.background = '#e3e3e3'
              }
            },
            expandedRowRender: (record) => {
              const values = {
                configureAlertViewState:
                  openViewStates[record.patientKey]?.configureAlertState,
                updateViewState: openViewStates[record.patientKey]?.updateState,
              }
              return (
                <PatientContext.Provider
                  value={{
                    ...values,
                    setOpenViewStates: setOpenViewStates,
                  }}
                >
                  <PatientTabs
                    status={record.status}
                    patientKey={record.patientKey}
                  />
                </PatientContext.Provider>
              )
            },
            expandIcon: ({ expanded, onExpand, record }) =>
              expanded ? (
                <DownOutlined onClick={(e) => onExpand(record, e)} />
              ) : (
                <RightOutlined onClick={(e) => onExpand(record, e)} />
              ),
          }}
          dataSource={filteredPatients}
          scroll={tableScroll} 
          pagination={{
            position: ['topLeft'],
            pageSizeOptions: [20, 50, 100, 200],
            defaultPageSize: 20,
          }}
        />
      </PatientViewContainer>
    </>
  )
}

export default PatientView
